import React, { useState, useMemo, useEffect, useRef } from 'react';
import styles from "./Email.module.css";
import {
  Avatar, Button, Checkbox, Input, Tooltip, ToggleButton,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  RadioGroup, Radio,
} from "@fluentui/react-components";
import {
  Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, MenuDivider,
} from "@fluentui/react-components";
import {
  Archive16Regular, ArrowReplyAll16Regular, ArrowForward16Regular, ArrowReply16Regular,
  Delete16Regular, Flag16Regular, Flag16Filled, FolderProhibited16Regular,
  MailInbox16Regular, Note16Regular, Send16Regular, Drafts16Regular,
  Add16Regular, ChevronDown16Regular, ChevronRight16Regular, ChevronLeft16Regular,
  NotepadSparkle16Regular, MoreHorizontal16Regular, Pin16Regular, Pin16Filled,
  CalendarAdd20Regular, MailRead20Regular, MailUnread20Regular,
  Print20Regular, Navigation20Regular, EyeOff16Regular, PersonProhibited16Regular,
  Broom16Regular, TextBulletListSquare16Regular, Flash16Regular, Tag16Regular,
  Search16Regular, Filter16Regular, ArrowSort16Regular, SelectAllOn16Regular,
  Attach16Regular, Folder16Regular, FolderAdd16Regular, History16Regular, Rss16Regular,
  FolderSearch16Regular, TextBold16Regular, TextItalic16Regular, TextUnderline16Regular,
  TextBulletList16Regular, TextNumberListLtr16Regular, Link16Regular, Dismiss16Regular,
  Checkmark16Regular, Document16Regular, ArrowDownload16Regular, Save16Regular,
  Keyboard16Regular, MailRead16Regular, MailUnread16Regular, ErrorCircle16Regular,
  Info16Regular, Mail16Regular, Compose16Regular, Warning16Filled, Settings16Regular, Delete16Filled,
  PeopleCommunity16Regular, Print16Regular, Clock16Regular, FolderArrowRight16Regular, ShieldError16Regular,
} from '@fluentui/react-icons';
import {
  Accordion, AccordionHeader, AccordionItem, AccordionPanel,
} from "@fluentui/react-components";
// API: all mail data comes from the backend through this client (X-Api-Key + X-Mailbox).
import * as mailApi from "./Api/emailApi";
import axios from "axios";
import useAuth from "../../hooks/useAuth";
import baseUrl from "../../constants/baseurl";

/* =====================================================================
   Types
   ===================================================================== */
type Att = { id?: string; name: string; size: number; type: string; file?: File };

/** The mailbox shape. Map your API response onto this (every field is required here on purpose). */
type Mail = {
  id: string;
  subject: string;
  from: string;
  fromName: string | null;
  to: string[];
  toNames: string[];
  cc: string[];
  ccNames: string[];
  date: string;
  snippet: string;
  body: string;
  isRead: boolean;
  isFlagged: boolean;
  attachments: Att[];
  folder: string;
  pinned: boolean;
  external: boolean;
  focused: boolean;
  categories: string[];
  snoozeUntil: number | null;
};

type Draft = {
  key: string;
  draftId: string | null;
  mode: "new" | "reply" | "replyAll" | "forward" | "meeting";
  to: string[]; cc: string[]; bcc: string[];
  subject: string; html: string; attachments: Att[];
  sourceId?: string;   // message being answered / forwarded
  attachFrom?: string; // message whose attachments (by id) are kept
};

type FolderDef = { id: string; label: string };
type SortKey = "dateDesc" | "dateAsc" | "from" | "subject";
type FilterKey = "all" | "unread" | "flagged" | "attachments";
type ToastState = { id: number; msg: string; undo: (() => void) | null } | null;
type Confirm =
  | { kind: "purge"; ids: string[] }
  | { kind: "empty"; folder: string }
  | { kind: "sweep"; sender: string; senderName: string }
  | { kind: "nosubject" }
  | { kind: "deleteFolder"; id: string }
  | { kind: "shortcuts" }
  | null;

/** What a local change must also tell the server. */
type Remote = { api?: () => Promise<unknown>; undo?: (() => Promise<unknown>) | false; reload?: boolean };

const PAGE_SIZE = 50;

/* =====================================================================
   Constants
   ===================================================================== */

const SYSTEM_FOLDERS: FolderDef[] = [
  { id: 'inbox', label: 'Inbox' },
  { id: 'drafts', label: 'Drafts' },
  { id: 'sent', label: 'Sent Items' },
  { id: 'trash', label: 'Deleted Items' },
  { id: 'junk', label: 'Junk Email' },
  { id: 'notes', label: 'Notes' },
  { id: 'archive', label: 'Archive' },
  { id: 'conversation', label: 'Conversation History' },
  { id: 'rss', label: 'RSS Feeds' },
  { id: 'search', label: 'Search Folders' },
];
const FAVORITES = ['inbox', 'sent', 'drafts', 'trash'];
const BRACKET_FOLDERS = ['drafts', 'junk', 'notes'];
const NO_MAIL_FOLDERS = ['notes', 'conversation', 'rss', 'search'];

const CATEGORIES: { id: string; label: string; color: string }[] = [
  { id: 'red', label: 'Red category', color: '#d13438' },
  { id: 'orange', label: 'Orange category', color: '#f7630c' },
  { id: 'yellow', label: 'Yellow category', color: '#eaa300' },
  { id: 'green', label: 'Green category', color: '#107c10' },
  { id: 'blue', label: 'Blue category', color: '#0f6cbd' },
  { id: 'purple', label: 'Purple category', color: '#8764b8' },
];

const SHORTCUTS: [string, string][] = [
  ['N', 'New message'], ['R', 'Reply'], ['Shift + R', 'Reply all'], ['F', 'Forward'],
  ['Delete', 'Delete'], ['E', 'Archive'], ['U', 'Mark as read / unread'], ['Q', 'Flag / unflag'],
  ['J or ↓', 'Next message'], ['K or ↑', 'Previous message'], ['/', 'Search'], ['Esc', 'Clear selection or search'],
];

/* =====================================================================
   Helpers
   ===================================================================== */
const textOf = (html: string) =>
  html.replace(/<style[\s\S]*?<\/style>/gi, ' ').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/\s+/g, ' ').trim();

const normSubject = (s: string) => s.replace(/^((re|fw|fwd):\s*)+/i, '').trim().toLowerCase();
const isEmail = (s: string) => /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/.test(s);
const sizeLabel = (n: number) => (n < 1024 ? `${n} B` : n < 1048576 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`);
const nameFromAddr = (a: string) => (a.split('@')[0] ?? a).replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const bucketOf = (iso: string): string => {
  const now = new Date();
  const d = new Date(iso);
  const day = startOfDay(d);
  const today = startOfDay(now);
  if (day >= today) return 'Today';
  if (day >= today - 86400000) return 'Yesterday';
  const dow = (now.getDay() + 6) % 7; // Monday = 0
  const weekStart = today - dow * 86400000;
  if (day >= weekStart) return 'This Week';
  if (day >= weekStart - 7 * 86400000) return 'Last Week';
  if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) return 'This Month';
  const last = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  if (d.getFullYear() === last.getFullYear() && d.getMonth() === last.getMonth()) return 'Last Month';
  return 'Older';
};
const BUCKET_ORDER = ['Today', 'Yesterday', 'This Week', 'Last Week', 'This Month', 'Last Month', 'Older'];

const listTime = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  if (startOfDay(d) === startOfDay(now)) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const wd = d.toLocaleDateString('en-US', { weekday: 'short' });
  const md = `${d.getMonth() + 1}/${d.getDate()}`;
  return d.getFullYear() === now.getFullYear() ? `${wd} ${md}` : `${md}/${d.getFullYear()}`;
};
const fullTime = (iso: string) =>
  new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }).replace(/,/g, '');

/** Strips scripts, handlers and (unless allowed) remote images from untrusted HTML. */
const sanitize = (html: string, allowRemote: boolean): { html: string; blocked: number } => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('script,style,iframe,object,embed,link,meta,form,base,svg').forEach((n) => n.remove());
  let blocked = 0;
  doc.querySelectorAll('*').forEach((el) => {
    Array.from(el.attributes).forEach((a) => {
      const n = a.name.toLowerCase();
      const v = a.value.trim().toLowerCase();
      if (n.startsWith('on')) el.removeAttribute(a.name);
      else if (['href', 'src', 'action', 'formaction', 'xlink:href'].includes(n) && v.startsWith('javascript:')) el.removeAttribute(a.name);
      else if (n === 'style' && /url\s*\(/i.test(v) && !allowRemote) { el.removeAttribute(a.name); blocked++; }
    });
  });
  doc.querySelectorAll('a').forEach((a) => { a.setAttribute('target', '_blank'); a.setAttribute('rel', 'noopener noreferrer nofollow'); });
  doc.querySelectorAll('img').forEach((img) => {
    const s = img.getAttribute('src') || '';
    if (/^(https?:)?\/\//i.test(s) && !allowRemote) {
      blocked++;
      img.removeAttribute('src');
      img.removeAttribute('srcset');
      img.setAttribute('style', 'display:inline-block;min-width:90px;min-height:22px;background:#f3f2f1;border:1px dashed #c8c6c4;color:#605e5c;font-size:12px');
    }
  });
  return { html: doc.body.innerHTML, blocked };
};

/** Local placeholder summary. Swap this for a call to your AI endpoint. */
const summarize = (m: Mail): string[] => {
  const t = textOf(m.body || m.snippet);
  const sentences = (t.match(/[^.!?]+[.!?]+/g) || [t]).map((s) => s.trim()).filter((s) => s.length > 25);
  const out = sentences.slice(0, 3).map((s) => (s.length > 150 ? `${s.slice(0, 147)}…` : s));
  if (out.length === 0) out.push(t.slice(0, 150) || 'No readable text in this message.');
  if (m.attachments.length) out.push(`${m.attachments.length} attachment${m.attachments.length > 1 ? 's' : ''}: ${m.attachments.map((a) => a.name).join(', ')}.`);
  return out;
};

const printMail = (m: Mail, bodyHtml: string) => {
  const w = window.open('', '_blank', 'width=800,height=900');
  if (!w) return;
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;');
  w.document.write(`<!doctype html><title>${esc(m.subject)}</title><body style="font-family:Segoe UI,Arial,sans-serif;font-size:14px;color:#242424;padding:24px"><h2 style="margin:0 0 12px">${esc(m.subject)}</h2><p style="margin:0 0 4px"><b>From:</b> ${esc(m.fromName ?? m.from)} &lt;${esc(m.from)}&gt;</p><p style="margin:0 0 4px"><b>To:</b> ${esc((m.to ?? []).join('; '))}</p><p style="margin:0 0 16px"><b>Sent:</b> ${esc(fullTime(m.date))}</p><hr>${bodyHtml}</body>`);
  w.document.close();
  w.focus();
  w.print();
};

/* Ids for local-only things (compose windows). Mail data itself comes from the API. */
let uid = 100;
const nid = () => `m${++uid}`;

/* =====================================================================
   Compose
   ===================================================================== */
type Contact = { name: string; address: string };

// API: GET /api/webmail/contacts?q=  (address book autocomplete)
const lookupContacts = (q: string, signal: AbortSignal): Promise<Contact[]> =>
  mailApi.searchContacts(q, signal).then((r) => r.map((c) => ({ name: c.name || nameFromAddr(c.email), address: c.email })));

const RecipientField: React.FC<{
  label: string; values: string[]; onChange: (v: string[]) => void; contacts: Contact[];
  lookup?: (q: string, signal: AbortSignal) => Promise<Contact[]>;
  inputRef?: React.Ref<HTMLInputElement>; aside?: React.ReactNode;
}> = ({ label, values, onChange, contacts, lookup, inputRef, aside }) => {
  const [text, setText] = useState('');
  const [focus, setFocus] = useState(false);
  const [remote, setRemote] = useState<Contact[]>([]);
  useEffect(() => {
    const t = text.trim();
    if (!lookup || !t) { setRemote([]); return; }
    const ac = new AbortController();
    const timer = window.setTimeout(() => { lookup(t, ac.signal).then(setRemote).catch(() => undefined); }, 200);
    return () => { window.clearTimeout(timer); ac.abort(); };
  }, [text, lookup]);
  const commit = (raw: string) => {
    const parts = raw.split(/[;,\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...values];
    parts.forEach((p) => { if (!next.includes(p)) next.push(p); });
    onChange(next);
    setText('');
  };
  const nameOf = (a: string) => [...contacts, ...remote].find((c) => c.address === a)?.name ?? a;
  const q = text.trim().toLowerCase();
  const pool = [...remote, ...contacts];
  const sugg = q ? pool.filter((c, i) => pool.findIndex((x) => x.address === c.address) === i && !values.includes(c.address) && `${c.name} ${c.address}`.toLowerCase().includes(q)).slice(0, 5) : [];
  return (
    <div className={styles.oxRcpt}>
      <span className={styles.oxRcptLabel}>{label}</span>
      <div className={styles.oxRcptBox}>
        {values.map((v) => (
          <span key={v} className={`${styles.oxChip} ${isEmail(v) ? '' : styles.oxChipBad}`} title={v}>
            {nameOf(v)}
            <button type="button" aria-label={`Remove ${v}`} onClick={() => onChange(values.filter((x) => x !== v))}><Dismiss16Regular /></button>
          </span>
        ))}
        <input
          ref={inputRef}
          className={styles.oxRcptInput}
          value={text}
          aria-label={label}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => setFocus(true)}
          onBlur={() => { setFocus(false); commit(text); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',' || e.key === ';' || (e.key === 'Tab' && text.trim())) {
              if (text.trim()) { e.preventDefault(); commit(text); }
            } else if (e.key === 'Backspace' && !text && values.length) onChange(values.slice(0, -1));
          }}
        />
        {focus && sugg.length > 0 && (
          <ul className={styles.oxSugg} role="listbox">
            {sugg.map((c) => (
              <li key={c.address} role="option" aria-selected="false" onMouseDown={(e) => { e.preventDefault(); onChange([...values, c.address]); setText(''); }}>
                <Avatar size={24} name={c.name} color="colorful" /><span><b>{c.name}</b><em>{c.address}</em></span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {aside}
    </div>
  );
};

const Compose: React.FC<{
  draft: Draft; contacts: Contact[];
  onSend: (d: Draft) => string | null;
  onSave: (d: Draft) => void;
  onDiscard: (d: Draft) => void;
  getRef: React.MutableRefObject<(() => Draft) | null>;
}> = ({ draft, contacts, onSend, onSave, onDiscard, getRef }) => {
  const [to, setTo] = useState(draft.to);
  const [cc, setCc] = useState(draft.cc);
  const [bcc, setBcc] = useState(draft.bcc);
  const [showCc, setShowCc] = useState(draft.cc.length > 0);
  const [showBcc, setShowBcc] = useState(draft.bcc.length > 0);
  const [subject, setSubject] = useState(draft.subject);
  const [atts, setAtts] = useState<Att[]>(draft.attachments);
  const [err, setErr] = useState<string | null>(null);
  const ed = useRef<HTMLDivElement>(null);
  const toRef = useRef<HTMLInputElement>(null);
  const file = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ed.current) ed.current.innerHTML = draft.html;
    if (draft.mode === 'new' || draft.to.length === 0) toRef.current?.focus();
    else if (ed.current) {
      ed.current.focus();
      const r = document.createRange(); r.setStart(ed.current, 0); r.collapse(true);
      const s = window.getSelection(); s?.removeAllRanges(); s?.addRange(r);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.key]);

  const collect = (): Draft => ({ ...draft, to, cc, bcc, subject, html: ed.current?.innerHTML ?? '', attachments: atts });
  const fmt = (cmd: string, val?: string) => { ed.current?.focus(); document.execCommand(cmd, false, val); };
  getRef.current = collect;
  const total = atts.reduce((t, a) => t + a.size, 0);

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const add: Att[] = Array.from(list).map((f) => ({ name: f.name, size: f.size, type: f.type || 'file', file: f }));
    if (total + add.reduce((t, a) => t + a.size, 0) > 25 * 1048576) { setErr('Attachments can’t be larger than 25 MB in total.'); return; }
    setErr(null);
    setAtts((p) => [...p, ...add]);
    if (file.current) file.current.value = '';
  };

  const send = () => {
    const all = [...to, ...cc, ...bcc];
    if (all.length === 0) { setErr('Add at least one recipient.'); return; }
    const bad = all.filter((a) => !isEmail(a));
    if (bad.length) { setErr(`“${bad[0]}” isn’t a valid email address.`); return; }
    setErr(onSend(collect()));
  };

  return (
    <div className={styles.oxCompose}>
      <div className={styles.oxCompHead}>
        <Button appearance="primary" size="small" icon={<Send16Regular />} onClick={send}>Send</Button>
        <Button appearance="subtle" size="small" icon={<Attach16Regular />} onClick={() => file.current?.click()}>Attach</Button>
        <Button appearance="subtle" size="small" icon={<Save16Regular />} onClick={() => onSave(collect())}>Save draft</Button>
        <span className={styles.oxGrow} />
        <Button appearance="subtle" size="small" icon={<Delete16Regular />} onClick={() => onDiscard(collect())}>Discard</Button>
        <input ref={file} type="file" multiple hidden onChange={(e) => addFiles(e.target.files)} />
      </div>
      {err && <div className={styles.oxCompErr} role="alert"><ErrorCircle16Regular />{err}</div>}
      <div className={styles.oxCompFields}>
        <RecipientField label="To" values={to} onChange={setTo} contacts={contacts} lookup={lookupContacts} inputRef={toRef}
          aside={<span className={styles.oxCcLinks}>{!showCc && <button type="button" onClick={() => setShowCc(true)}>Cc</button>}{!showBcc && <button type="button" onClick={() => setShowBcc(true)}>Bcc</button>}</span>} />
        {showCc && <RecipientField label="Cc" values={cc} onChange={setCc} contacts={contacts} lookup={lookupContacts} />}
        {showBcc && <RecipientField label="Bcc" values={bcc} onChange={setBcc} contacts={contacts} lookup={lookupContacts} />}
        <div className={styles.oxSubjRow}>
          <input className={styles.oxSubject} placeholder="Add a subject" aria-label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
      </div>
      <div className={styles.oxFmt} onMouseDown={(e) => e.preventDefault()}>
        <Tooltip content="Bold" relationship="label"><Button appearance="subtle" size="small" icon={<TextBold16Regular />} onClick={() => fmt('bold')} /></Tooltip>
        <Tooltip content="Italic" relationship="label"><Button appearance="subtle" size="small" icon={<TextItalic16Regular />} onClick={() => fmt('italic')} /></Tooltip>
        <Tooltip content="Underline" relationship="label"><Button appearance="subtle" size="small" icon={<TextUnderline16Regular />} onClick={() => fmt('underline')} /></Tooltip>
        <span className={styles.oxFmtSep} />
        <Tooltip content="Bullets" relationship="label"><Button appearance="subtle" size="small" icon={<TextBulletList16Regular />} onClick={() => fmt('insertUnorderedList')} /></Tooltip>
        <Tooltip content="Numbering" relationship="label"><Button appearance="subtle" size="small" icon={<TextNumberListLtr16Regular />} onClick={() => fmt('insertOrderedList')} /></Tooltip>
        <Tooltip content="Insert link" relationship="label"><Button appearance="subtle" size="small" icon={<Link16Regular />} onClick={() => { const u = window.prompt('Link address (https://…)'); if (u && /^(https?:|mailto:)/i.test(u.trim())) fmt('createLink', u.trim()); }} /></Tooltip>
      </div>
      <div ref={ed} className={styles.oxEditor} contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" aria-label="Message body" data-placeholder="Write your message" />
      {atts.length > 0 && (
        <div className={styles.oxCompAtts}>
          {atts.map((a, i) => (
            <span key={`${a.name}${i}`} className={styles.oxAtt}><Document16Regular />{a.name}<em>{sizeLabel(a.size)}</em>
              <button type="button" aria-label={`Remove ${a.name}`} onClick={() => setAtts((p) => p.filter((_, j) => j !== i))}><Dismiss16Regular /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/* =====================================================================
   Ribbon
   ===================================================================== */
type Actions = {
  newMail: () => void; del: () => void; archive: () => void; ignore: () => void; block: () => void;
  report: (k: 'junk' | 'phishing' | 'notjunk') => void;
  reply: () => void; replyAll: () => void; forward: () => void; meeting: () => void;
  moveTo: (id: string) => void; newFolder: () => void; sweep: () => void; rule: (k: 'archive' | 'junk' | 'delete') => void;
  quick: (k: 'done' | 'replydelete' | 'flagread') => void;
  toggleRead: () => void; flag: (on: boolean) => void; pin: () => void; category: (id: string) => void; snooze: (k: string) => void;
  print: () => void; saveAs: () => void; shortcuts: () => void;
};
type ViewState = {
  nav: boolean; setNav: (v: boolean) => void;
  pane: 'right' | 'bottom' | 'off'; setPane: (v: 'right' | 'bottom' | 'off') => void;
  preview: boolean; setPreview: (v: boolean) => void;
  group: boolean; setGroup: (v: boolean) => void;
  focusedOn: boolean; setFocusedOn: (v: boolean) => void;
  compact: boolean; setCompact: (v: boolean) => void;
  sort: SortKey; setSort: (v: SortKey) => void;
};

/** Icon button; with children it becomes a menu button (icon + chevron). */
const Ic: React.FC<{ icon: React.ReactElement; label: string; onClick?: () => void; disabled?: boolean; color?: string; children?: React.ReactNode }> = ({ icon, label, onClick, disabled, color, children }) => {
  const btn = (
    <Button appearance="subtle" size="small" className={styles.oxIc} title={label} aria-label={label} disabled={disabled ?? false}
      icon={<span className={styles.oxIcGlyph} style={color && !disabled ? { color } : {}}>{icon}</span>} {...(onClick ? { onClick } : {})}>
      {children ? <ChevronDown16Regular className={styles.oxIcChev} /> : null}
    </Button>
  );
  if (!children) return btn;
  return <Menu><MenuTrigger disableButtonEnhancement>{btn}</MenuTrigger><MenuPopover><MenuList>{children}</MenuList></MenuPopover></Menu>;
};

/** Icon + text button (Read / Unread, View and File commands). */
const Lb: React.FC<{ icon: React.ReactElement; label: string; onClick?: () => void; disabled?: boolean; children?: React.ReactNode }> = ({ icon, label, onClick, disabled, children }) => {
  const btn = (
    <Button appearance="subtle" size="small" className={styles.oxLb} icon={icon} disabled={disabled ?? false} {...(onClick ? { onClick } : {})}>
      {label}{children ? <ChevronDown16Regular className={styles.oxIcChev} /> : null}
    </Button>
  );
  if (!children) return btn;
  return <Menu><MenuTrigger disableButtonEnhancement>{btn}</MenuTrigger><MenuPopover><MenuList>{children}</MenuList></MenuPopover></Menu>;
};

const Tog: React.FC<{ icon: React.ReactElement; label: string; on: boolean; set: (v: boolean) => void }> = ({ icon, label, on, set }) => (
  <ToggleButton size="small" appearance="subtle" className={styles.oxLb} icon={icon} checked={on} onClick={() => set(!on)}>{label}</ToggleButton>
);

const Sep: React.FC = () => <span className={styles.oxSep} />;

const Ribbon: React.FC<{
  has: boolean; hasOne: boolean; readIs: 'read' | 'unread'; pinned: boolean; flagged: boolean;
  folders: FolderDef[]; inJunk: boolean; catOn: (id: string) => boolean;
  a: Actions; v: ViewState;
}> = ({ has, hasOne, readIs, pinned, flagged, folders, inJunk, catOn, a, v }) => {
  const [tab, setTab] = useState<'file' | 'home' | 'view' | 'help'>('home');
  const tabs: ['file' | 'home' | 'view' | 'help', string][] = [['file', 'File'], ['home', 'Home'], ['view', 'View'], ['help', 'Help']];
  const tick = (on: boolean) => (on ? <Checkmark16Regular /> : null);
  return (
    <>
      <div className={styles.oxTabs} role="tablist">
        <Button appearance="subtle" size="small" className={styles.oxHam} icon={<Navigation20Regular />} title="Show or hide folders" aria-label="Show or hide folders" onClick={() => v.setNav(!v.nav)} />
        {tabs.map(([id, l]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} className={`${styles.oxTab} ${tab === id ? styles.oxTabOn : ''}`} onClick={() => setTab(id)}>{l}</button>
        ))}
      </div>
      <div className={styles.oxBar} role="toolbar" aria-label="Commands">
        {tab === 'home' && (
          <>
            <div className={styles.oxNew}>
              <Button appearance="primary" size="small" icon={<Compose16Regular />} onClick={a.newMail} title="New message (N)">New mail</Button>
              <Menu>
                <MenuTrigger disableButtonEnhancement><Button appearance="primary" size="small" icon={<ChevronDown16Regular />} aria-label="More new items" /></MenuTrigger>
                <MenuPopover><MenuList>
                  <MenuItem icon={<Mail16Regular />} onClick={a.newMail}>Mail message</MenuItem>
                  <MenuItem icon={<FolderAdd16Regular />} onClick={a.newFolder}>Folder</MenuItem>
                </MenuList></MenuPopover>
              </Menu>
            </div>
            <Sep />
            <Ic icon={<Delete16Regular />} label="Delete" disabled={!has} onClick={a.del} />
            <Ic icon={<Archive16Regular />} label="Archive (E)" disabled={!has} onClick={a.archive} color="#107c10" />
            <Ic icon={<ShieldError16Regular />} label="Report" disabled={!has} color="#c50f1f">
              {inJunk
                ? <MenuItem onClick={() => a.report('notjunk')}>Not junk</MenuItem>
                : (<><MenuItem onClick={() => a.report('junk')}>Junk</MenuItem><MenuItem onClick={() => a.report('phishing')}>Phishing</MenuItem></>)}
            </Ic>
            <Ic icon={<Broom16Regular />} label="Sweep" disabled={!hasOne} onClick={a.sweep} />
            <Ic icon={<FolderArrowRight16Regular />} label="Move to" disabled={!has} color="#0f6cbd">
              {folders.map((f) => <MenuItem key={f.id} icon={<FolderGlyph id={f.id} />} onClick={() => a.moveTo(f.id)}>{f.label}</MenuItem>)}
              <MenuDivider />
              <MenuItem icon={<FolderAdd16Regular />} onClick={a.newFolder}>New folder…</MenuItem>
            </Ic>
            <Sep />
            <Ic icon={<ArrowReply16Regular />} label="Reply (R)" disabled={!hasOne} onClick={a.reply} color="#0f6cbd" />
            <Ic icon={<ArrowReplyAll16Regular />} label="Reply all (Shift+R)" disabled={!hasOne} onClick={a.replyAll} color="#0f6cbd" />
            <Ic icon={<ArrowForward16Regular />} label="Forward (F)" disabled={!hasOne} color="#0f6cbd" onClick={a.forward}>
              <MenuItem onClick={a.forward}>Forward</MenuItem>
              <MenuItem onClick={a.meeting}>Reply with meeting</MenuItem>
            </Ic>
            <Sep />
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button appearance="subtle" size="small" className={`${styles.oxLb} ${styles.oxQuick}`} icon={<Flash16Regular className={styles.oxBolt} />} disabled={!has}>
                  Quick steps<ChevronDown16Regular className={styles.oxIcChev} />
                </Button>
              </MenuTrigger>
              <MenuPopover><MenuList>
                <MenuItem onClick={() => a.quick('done')}>Done: mark read and archive</MenuItem>
                <MenuItem disabled={!hasOne} onClick={() => a.quick('replydelete')}>Reply and delete</MenuItem>
                <MenuItem onClick={() => a.quick('flagread')}>Flag and mark read</MenuItem>
              </MenuList></MenuPopover>
            </Menu>
            <Lb icon={readIs === 'read' ? <MailRead20Regular /> : <MailUnread20Regular />} label="Read / Unread" disabled={!has} onClick={a.toggleRead} />
            <Ic icon={<Tag16Regular />} label="Categorize" disabled={!has}>
              {CATEGORIES.map((c) => (
                <MenuItem key={c.id} icon={<span className={styles.oxSwatch} style={{ background: c.color }} />} secondaryContent={catOn(c.id) ? <Checkmark16Regular /> : null} onClick={() => a.category(c.id)}>{c.label}</MenuItem>
              ))}
            </Ic>
            <Ic icon={flagged ? <Flag16Filled /> : <Flag16Regular />} label="Flag" disabled={!has} color="#c50f1f">
              <MenuItem icon={<Flag16Filled className={styles.oxFlagOn} />} onClick={() => a.flag(true)}>Flag message</MenuItem>
              <MenuItem icon={<Flag16Regular />} onClick={() => a.flag(false)}>Clear flag</MenuItem>
            </Ic>
            <Ic icon={pinned ? <Pin16Filled /> : <Pin16Regular />} label={pinned ? 'Unpin' : 'Pin'} disabled={!has} onClick={a.pin} color="#0f6cbd" />
            <Ic icon={<Clock16Regular />} label="Snooze" disabled={!has}>
              <MenuItem onClick={() => a.snooze('later')}>Later today</MenuItem>
              <MenuItem onClick={() => a.snooze('tomorrow')}>Tomorrow</MenuItem>
              <MenuItem onClick={() => a.snooze('weekend')}>This weekend</MenuItem>
              <MenuItem onClick={() => a.snooze('nextweek')}>Next week</MenuItem>
            </Ic>
            <Sep />
            <Ic icon={<Print16Regular />} label="Print" disabled={!hasOne} onClick={a.print} />
            <Ic icon={<MoreHorizontal16Regular />} label="More actions">
              <MenuItem icon={<EyeOff16Regular />} disabled={!hasOne} onClick={a.ignore}>Ignore conversation</MenuItem>
              <MenuItem icon={<PersonProhibited16Regular />} disabled={!hasOne} onClick={a.block}>Block sender</MenuItem>
              <MenuItem icon={<CalendarAdd20Regular />} disabled={!hasOne} onClick={a.meeting}>Reply with meeting</MenuItem>
              <MenuDivider />
              <MenuItem icon={<TextBulletListSquare16Regular />} disabled={!hasOne} onClick={() => a.rule('archive')}>Rule: always move this sender to Archive</MenuItem>
              <MenuItem icon={<TextBulletListSquare16Regular />} disabled={!hasOne} onClick={() => a.rule('junk')}>Rule: always move this sender to Junk</MenuItem>
              <MenuItem icon={<TextBulletListSquare16Regular />} disabled={!hasOne} onClick={() => a.rule('delete')}>Rule: always delete this sender</MenuItem>
              <MenuDivider />
              <MenuItem icon={<ArrowDownload16Regular />} disabled={!hasOne} onClick={a.saveAs}>Save as .eml</MenuItem>
              <MenuItem icon={<Keyboard16Regular />} onClick={a.shortcuts}>Keyboard shortcuts</MenuItem>
            </Ic>
          </>
        )}
        {tab === 'view' && (
          <>
            <Tog icon={<Navigation20Regular />} label="Folder pane" on={v.nav} set={v.setNav} />
            <Lb icon={<MailInbox16Regular />} label="Reading pane">
              <MenuItem icon={tick(v.pane === 'right')} onClick={() => v.setPane('right')}>Right</MenuItem>
              <MenuItem icon={tick(v.pane === 'bottom')} onClick={() => v.setPane('bottom')}>Bottom</MenuItem>
              <MenuItem icon={tick(v.pane === 'off')} onClick={() => v.setPane('off')}>Off</MenuItem>
            </Lb>
            <Sep />
            <Tog icon={<Mail16Regular />} label="Message preview" on={v.preview} set={v.setPreview} />
            <Tog icon={<History16Regular />} label="Group by date" on={v.group} set={v.setGroup} />
            <Tog icon={<MailInbox16Regular />} label="Focused Inbox" on={v.focusedOn} set={v.setFocusedOn} />
            <Tog icon={<TextBulletList16Regular />} label="Compact list" on={v.compact} set={v.setCompact} />
            <Sep />
            <Lb icon={<ArrowSort16Regular />} label="Sort by">
              <MenuItem icon={tick(v.sort === 'dateDesc')} onClick={() => v.setSort('dateDesc')}>Date: newest on top</MenuItem>
              <MenuItem icon={tick(v.sort === 'dateAsc')} onClick={() => v.setSort('dateAsc')}>Date: oldest on top</MenuItem>
              <MenuItem icon={tick(v.sort === 'from')} onClick={() => v.setSort('from')}>From</MenuItem>
              <MenuItem icon={tick(v.sort === 'subject')} onClick={() => v.setSort('subject')}>Subject</MenuItem>
            </Lb>
          </>
        )}
        {tab === 'file' && (
          <>
            <Lb icon={<ArrowDownload16Regular />} label="Save as .eml" disabled={!hasOne} onClick={a.saveAs} />
            <Lb icon={<Print16Regular />} label="Print" disabled={!hasOne} onClick={a.print} />
          </>
        )}
        {tab === 'help' && <Lb icon={<Keyboard16Regular />} label="Keyboard shortcuts" onClick={a.shortcuts} />}
      </div>
    </>
  );
};

const FolderGlyph: React.FC<{ id: string }> = ({ id }) => {
  switch (id) {
    case 'inbox': return <MailInbox16Regular />;
    case 'sent': return <Send16Regular />;
    case 'drafts': return <Drafts16Regular />;
    case 'trash': return <Delete16Regular />;
    case 'junk': return <FolderProhibited16Regular />;
    case 'notes': return <Note16Regular />;
    case 'archive': return <Archive16Regular />;
    case 'conversation': return <History16Regular />;
    case 'rss': return <Rss16Regular />;
    case 'search': return <FolderSearch16Regular />;
    default: return <Folder16Regular />;
  }
};

/* =====================================================================
   Message row
   ===================================================================== */
interface MailItemProps {
  mail: Mail;
  folder: string;
  isChecked: boolean;
  anyChecked: boolean;
  isSelected: boolean;
  preview: boolean;
  onCheckChange: (id: string, checked: boolean) => void;
  onFlagChange: (id: string, flagged: boolean) => void;
  onPinChange: (id: string) => void;
  onDelete: (id: string) => void;
  onSelect: (mail: Mail) => void;
  onToggleRead: (id: string) => void;
  onArchive: (id: string) => void;
}

const MailItem: React.FC<MailItemProps> = ({
  mail, folder, isChecked, anyChecked, isSelected, preview,
  onCheckChange, onFlagChange, onPinChange, onDelete, onSelect, onToggleRead, onArchive,
}) => {
  const outgoing = folder === 'sent' || folder === 'drafts';
  const who = outgoing ? (mail.toNames?.length ? mail.toNames.join('; ') : (mail.to ?? []).join('; ')) || 'No recipient' : (mail.fromName ?? mail.from);
  const avatarName = outgoing ? who : (mail.fromName ?? mail.from);
  return (
    <div
      role="option"
      aria-selected={isSelected}
      data-mail-id={mail.id}
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('text/mail-id', mail.id); e.dataTransfer.effectAllowed = 'move'; }}
      className={`${styles.mailRow} ${styles.oxRow} ${isSelected ? styles.mailSelected : ''} ${mail.isFlagged ? styles.mailFlagged : ''} ${!mail.isRead ? styles.oxUnread : ''} ${isChecked ? styles.oxChecked : ''}`}
      onClick={() => onSelect(mail)}
    >
      <div className={`${styles.mailLeft} ${styles.oxLeft} ${isChecked || anyChecked || isSelected ? styles.oxLeftCheck : ''}`} onClick={(e) => e.stopPropagation()}>
        <span className={styles.oxAv}><Avatar size={36} name={avatarName} color="colorful" /></span>
        <span className={styles.oxCk}><Checkbox checked={isChecked} aria-label="Select message" onChange={(_, d) => onCheckChange(mail.id, !!d.checked)} /></span>
      </div>

      <div className={styles.mailContent}>
        <div className={styles.mailTopLine}>
          <span className={styles.oxFromWrap}>
            {mail.external && !outgoing && <span className={styles.oxExt}>External</span>}
            {mail.folder === 'drafts' && <span className={styles.oxDraftTag}>Draft</span>}
            <span className={styles.mailFrom}>{who}</span>
          </span>
          <span className={styles.oxInd}>
            {mail.categories.map((c) => <i key={c} className={styles.oxDot} style={{ background: CATEGORIES.find((x) => x.id === c)?.color }} />)}
            {mail.attachments.length > 0 && <Attach16Regular />}
            {mail.isFlagged && <Flag16Filled className={styles.oxFlagOn} />}
            {mail.pinned && <Pin16Filled className={styles.oxPinOn} />}
          </span>
          <span className={styles.oxHov} onClick={(e) => e.stopPropagation()}>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button appearance="subtle" size="small" icon={<Settings16Regular />} title="Quick actions" aria-label="Quick actions" className={styles.oxHovBtn} />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem icon={mail.isRead ? <MailUnread16Regular /> : <MailRead16Regular />} onClick={() => onToggleRead(mail.id)}>{mail.isRead ? 'Mark as unread' : 'Mark as read'}</MenuItem>
                  <MenuItem icon={<Archive16Regular />} onClick={() => onArchive(mail.id)}>Archive</MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
            <Button appearance="subtle" size="small" icon={mail.isFlagged ? <Flag16Filled /> : <Flag16Regular />} title={mail.isFlagged ? 'Unflag' : 'Flag'} aria-label={mail.isFlagged ? 'Unflag' : 'Flag'}
              onClick={() => onFlagChange(mail.id, !mail.isFlagged)} className={`${styles.oxHovBtn} ${mail.isFlagged ? styles.oxHovFlag : ''}`} />
            <Button appearance="subtle" size="small" icon={mail.pinned ? <Pin16Filled /> : <Pin16Regular />} title={mail.pinned ? 'Unpin' : 'Pin'} aria-label={mail.pinned ? 'Unpin' : 'Pin'}
              onClick={() => onPinChange(mail.id)} className={`${styles.oxHovBtn} ${mail.pinned ? styles.oxHovPin : ''}`} />
          </span>
        </div>
        <div className={styles.oxSubjLine}>
          <span className={styles.mailSubject}>{mail.subject || '(No subject)'}</span>
          <span className={styles.mailDate}>{listTime(mail.date)}</span>
        </div>
        {preview && <div className={styles.mailSnippet}>{mail.snippet}</div>}
      </div>

      <span className={styles.oxTrashWrap} onClick={(e) => e.stopPropagation()}>
        <Tooltip content="Delete" relationship="label" positioning="below-end">
          <Button appearance="subtle" size="small" icon={<Delete16Filled />} aria-label="Delete" onClick={() => onDelete(mail.id)} className={styles.oxTrash} />
        </Tooltip>
      </span>
    </div>
  );
};

/* =====================================================================
   Reader
   ===================================================================== */
const Reader: React.FC<{
  mail: Mail; allowRemote: boolean;
  summary: string[] | null; onCloseSummary: () => void;
  a: Actions; onTrust: () => void; onBlock: () => void; warnOff: boolean; onDismissWarn: () => void; onShowContent: () => void; onDownload: (att: Att) => void;
  onToggleRead: () => void; onFlag: () => void; onPin: () => void;
}> = ({ mail, allowRemote, summary, onCloseSummary, a, onTrust, onBlock, warnOff, onDismissWarn, onShowContent, onDownload, onToggleRead, onFlag, onPin }) => {
  const clean = useMemo(() => sanitize(mail.body || mail.snippet, allowRemote), [mail.body, mail.snippet, allowRemote]);
  const names = (mail.to ?? []).map((t, i) => mail.toNames?.[i] || t);
  const ccNames = (mail.cc ?? []).map((t, i) => mail.ccNames?.[i] || t);
  return (
    <>
      <div className={`${styles.readerHeader} ${styles.oxRdHead}`}>
        <Avatar size={48} name={mail.fromName ?? mail.from} color="colorful" className={styles.oxRdAv} />
        <div className={styles.readerFromBlock}>
          <div className={styles.readerFrom}>
            {mail.fromName ?? mail.from}
            <span className={styles.readerEmail}>&lt;{mail.from}&gt;</span>
          </div>
        </div>
        <div className={styles.oxRdActs}>
          <Tooltip content="Reply (R)" relationship="label"><Button appearance="subtle" size="small" icon={<ArrowReply16Regular />} onClick={a.reply} /></Tooltip>
          <Tooltip content="Reply all (Shift+R)" relationship="label"><Button appearance="subtle" size="small" icon={<ArrowReplyAll16Regular />} onClick={a.replyAll} /></Tooltip>
          <Tooltip content="Forward (F)" relationship="label"><Button appearance="subtle" size="small" icon={<ArrowForward16Regular />} onClick={a.forward} /></Tooltip>
          <Menu>
            <MenuTrigger disableButtonEnhancement><Button appearance="subtle" size="small" icon={<MoreHorizontal16Regular />} aria-label="More actions" /></MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={mail.isRead ? <MailUnread16Regular /> : <MailRead16Regular />} onClick={onToggleRead}>{mail.isRead ? 'Mark as unread' : 'Mark as read'}</MenuItem>
                <MenuItem icon={mail.isFlagged ? <Flag16Regular /> : <Flag16Filled />} onClick={onFlag}>{mail.isFlagged ? 'Clear flag' : 'Flag'}</MenuItem>
                <MenuItem icon={mail.pinned ? <Pin16Regular /> : <Pin16Filled />} onClick={onPin}>{mail.pinned ? 'Unpin' : 'Pin'}</MenuItem>
                <MenuItem icon={<Archive16Regular />} onClick={a.archive}>Archive</MenuItem>
                <MenuDivider />
                <MenuItem icon={<Print20Regular />} onClick={a.print}>Print</MenuItem>
                <MenuItem icon={<ArrowDownload16Regular />} onClick={a.saveAs}>Save as .eml</MenuItem>
                <MenuItem icon={<FolderProhibited16Regular />} onClick={() => a.report('junk')}>Report junk</MenuItem>
                <MenuDivider />
                <MenuItem icon={<Delete16Regular />} onClick={a.del}>Delete</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
        <div className={styles.oxRdTo}>
          <span>To: {names.join('; ') || 'me'}{ccNames.length > 0 && <> &nbsp;Cc: {ccNames.join('; ')}</>}</span>
          {mail.categories.map((c) => { const k = CATEGORIES.find((x) => x.id === c); return <i key={c} className={styles.oxCatTag} style={{ background: k?.color }}>{k?.label.replace(' category', '')}</i>; })}
        </div>
        <div className={styles.readerDate}>{fullTime(mail.date)}</div>
      </div>

      {summary && (
        <div className={styles.oxSummary}>
          <div className={styles.oxSummaryHead}><NotepadSparkle16Regular /><b>Summary</b><span className={styles.oxGrow} />
            <Button appearance="subtle" size="small" icon={<Dismiss16Regular />} aria-label="Close summary" onClick={onCloseSummary} /></div>
          <ul>{summary.map((s, i) => <li key={i}>{s}</li>)}</ul>
          <p>Generated on this device from the message text. Check important details in the original.</p>
        </div>
      )}

      {mail.external && !warnOff ? (
        <div className={styles.oxWarn} role="status">
          <Warning16Filled />
          <span>This sender {mail.from} is from outside your organization.</span>
          <Button size="small" onClick={onBlock}>Block sender</Button>
          {!allowRemote && <Button size="small" onClick={onShowContent}>Show blocked content</Button>}
          <Button appearance="subtle" size="small" icon={<Dismiss16Regular />} aria-label="Dismiss warning for this sender" title="Don’t show again for this sender" onClick={onDismissWarn} />
        </div>
      ) : clean.blocked > 0 && !allowRemote && (
        <div className={styles.oxBanner} role="status">
          <Info16Regular />
          <span>Some content in this message has been blocked because the sender isn’t in your Safe senders list.</span>
          <Button size="small" onClick={onTrust}>Trust sender</Button>
          <Button size="small" onClick={onShowContent}>Show blocked content</Button>
        </div>
      )}

      <div className={`${styles.readerBody} ${styles.oxBody}`} dangerouslySetInnerHTML={{ __html: clean.html }} />

      {mail.attachments.length > 0 && (
        <div className={styles.oxAttBar}>
          {mail.attachments.map((at) => (
            <button key={at.id ?? at.name} type="button" className={styles.oxAttCard} onClick={() => onDownload(at)}>
              <Document16Regular /><span><b>{at.name}</b><em>{sizeLabel(at.size)}</em></span><ArrowDownload16Regular />
            </button>
          ))}
        </div>
      )}

      <div className={styles.oxRdFoot}>
        <Button appearance="secondary" size='small' icon={<ArrowReply16Regular />} onClick={a.reply}>Reply</Button>
        <Button appearance="secondary" size='small' icon={<ArrowReplyAll16Regular />} onClick={a.replyAll}>Reply all</Button>
        <Button appearance="secondary" size='small' icon={<ArrowForward16Regular />} onClick={a.forward}>Forward</Button>
      </div>
    </>
  );
};

/* =====================================================================
   Email (main)
   ===================================================================== */
const quoteOf = (m: Mail) =>
  `<br><br><div>On ${fullTime(m.date)}, ${(m.fromName ?? m.from).replace(/</g, '&lt;')} &lt;${m.from}&gt; wrote:<blockquote style="margin:8px 0 0 4px;padding-left:12px;border-left:2px solid #c8c6c4;color:#424242">${sanitize(m.body, false).html}</blockquote></div>`;

type EmailProps = {
  /** Tenant API key (same one the rest of the app uses). */
  apiKey?: string | null;
  /** The mailbox to show, e.g. activeAccount.email. */
  mailbox?: string | null;
  /** Optional display name for the signature. */
  displayName?: string;
};

export const MailClient: React.FC<EmailProps> = ({ apiKey = null, mailbox = null, displayName }) => {
  const ME = (mailbox ?? '').trim().toLowerCase();
  const ME_NAME = displayName || nameFromAddr(ME);
  const SIG = `<br><div>Regards,<br>${ME_NAME}</div>`;
  useMemo(() => mailApi.configureMail(apiKey, ME || null), [apiKey, ME]);
  const connected = !!apiKey && !!ME;

  const [emails, setEmails] = useState<Mail[]>([]);
  const emailsRef = useRef<Mail[]>(emails);
  emailsRef.current = emails;

  // API: folders (with unread/total counts) come from GET /api/webmail/folders
  const [apiFolders, setApiFolders] = useState<mailApi.ApiFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [serverOthers, setServerOthers] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [proxiedIds, setProxiedIds] = useState<Set<string>>(new Set());
  const proxiedRef = useRef(proxiedIds);
  proxiedRef.current = proxiedIds;
  const proxying = useRef<Set<string>>(new Set());
  const pageNo = useRef(1);
  const folderTimer = useRef<number | undefined>(undefined);
  const creatingFolder = useRef(false);
  const sending = useRef(false);
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [category, setCategory] = useState<'focused' | 'other'>('focused');
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [sort, setSort] = useState<SortKey>('dateDesc');
  const [pane, setPane] = useState<'right' | 'bottom' | 'off'>('right');
  const [preview, setPreview] = useState(true);
  const [group, setGroup] = useState(true);
  const [focusedOn, setFocusedOn] = useState(true);
  const [compact, setCompact] = useState(false);
  const [nav, setNav] = useState(typeof window === 'undefined' ? true : window.innerWidth > 900);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [openItems, setOpenItems] = useState<string[]>(['favorites', 'account-0']);
  const [compose, setCompose] = useState<Draft | null>(null);
  const [toast, setToast] = useState<ToastState>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [sweepMode, setSweepMode] = useState('all');
  const [trusted, setTrusted] = useState<Set<string>>(new Set());
  const [quiet, setQuiet] = useState<Set<string>>(new Set()); // senders whose external-sender warning was dismissed
  const [shown, setShown] = useState<Set<string>>(new Set());
  const [summaryFor, setSummaryFor] = useState<string | null>(null);
  const [showReader, setShowReader] = useState(false);
  const [addingFolder, setAddingFolder] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [dropFolder, setDropFolder] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const composeGetter = useRef<(() => Draft) | null>(null);
  const pendingSend = useRef<Draft | null>(null);
  const pendingDelete = useRef<string | null>(null);
  const toastId = useRef(0);
  const toastTimer = useRef<number | undefined>(undefined);
    const keyRef = useRef<(e: KeyboardEvent) => void>(() => undefined);

  const customFolders = useMemo<FolderDef[]>(
    () => apiFolders.filter((f) => !f.isSystem && !f.isVirtual).map((f) => ({ id: f.id, label: f.label })), [apiFolders]);
  const folders = useMemo(() => [...SYSTEM_FOLDERS, ...customFolders], [customFolders]);
  const folderLabel = (id: string) => folders.find((f) => f.id === id)?.label ?? id;

  useEffect(() => { const t = window.setInterval(() => setTick((n) => n + 1), 30000); return () => window.clearInterval(t); }, []);
  useEffect(() => () => window.clearTimeout(toastTimer.current), []);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const h = (e: MediaQueryListEvent) => setNav(!e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);

  /* ---------- API: loading ---------- */
  const isTrusted = (a: string) => trusted.has(a.toLowerCase()) || trusted.has('@' + (a.split('@')[1] ?? '').toLowerCase());
  const isQuiet = (a: string) => quiet.has(a.toLowerCase()) || quiet.has('@' + (a.split('@')[1] ?? '').toLowerCase());
  const allowRemoteFor = (m: Mail) => (isTrusted(m.from) || shown.has(m.id)) && proxiedIds.has(m.id);

  const P = useRef({ activeFolder, filter, sort, q: debouncedSearch });
  P.current = { activeFolder, filter, sort, q: debouncedSearch };

  const refreshFoldersNow = () => {
    if (!connected) return;
    mailApi.getFolders().then(setApiFolders).catch(() => undefined);
  };
  const refreshFolders = () => {
    window.clearTimeout(folderTimer.current);
    folderTimer.current = window.setTimeout(refreshFoldersNow, 300);
  };

  const mergeList = (folder: string, msgs: mailApi.ApiMail[], append: boolean) => {
    const cur = emailsRef.current;
    const old = new Map(cur.map((m) => [m.id, m] as const));
    const mapped = msgs.map((x) => ({ ...x, folder, attachments: x.attachments as Att[], body: x.body || old.get(x.id)?.body || '' }) as Mail);
    const ids2 = new Set(mapped.map((m) => m.id));
    setAll([...cur.filter((m) => (append || m.folder !== folder) && !ids2.has(m.id)), ...mapped]);
  };
  const fetchPage = (page: number, signal?: AbortSignal) => {
    const p = P.current;
    return mailApi.listMessages({ folder: p.activeFolder, filter: p.filter, sort: p.sort, q: p.q || undefined, page, pageSize: PAGE_SIZE }, signal);
  };
  const reloadNow = () => {
    if (!connected || NO_MAIL_FOLDERS.includes(P.current.activeFolder)) return;
    const folder = P.current.activeFolder;
    fetchPage(1).then((r) => { if (P.current.activeFolder !== folder) return; mergeList(folder, r.messages, false); setHasMore(r.hasMore); setServerOthers(r.othersUnread); pageNo.current = 1; }).catch(() => undefined);
  };
  const loadMore = () => {
    const folder = P.current.activeFolder;
    const next = pageNo.current + 1;
    fetchPage(next).then((r) => { if (P.current.activeFolder !== folder) return; mergeList(folder, r.messages, true); pageNo.current = next; setHasMore(r.hasMore); }).catch(() => say('Couldn’t load more messages.'));
  };
  const loadBody = (id: string) => {
    mailApi.getMessage(id).then((f) => {
      setAll(emailsRef.current.map((x) => (x.id !== id ? x : {
        ...x, body: proxiedRef.current.has(id) ? x.body : f.body, attachments: f.attachments as Att[],
        to: f.to, toNames: f.toNames, cc: f.cc, ccNames: f.ccNames,
      })));
    }).catch((e) => { if (!mailApi.isAbort(e)) say('Couldn’t open this message.'); });
  };

  // new mailbox / key: start clean, load folders and the sender lists
  useEffect(() => {
    setAll([]); setSelectedId(null); setApiFolders([]); setProxiedIds(new Set()); proxying.current.clear();
    setTrusted(new Set()); setQuiet(new Set());
    if (!connected) return;
    refreshFoldersNow();
    mailApi.getRules().then((rules) => {
      const safe = new Set<string>(); const quietSet = new Set<string>();
      rules.forEach((r: { pattern: string; ruleType: string }) => {
        const p = String(r.pattern).toLowerCase();
        if (r.ruleType === 'Safe') safe.add(p); else if (r.ruleType === 'Quiet') quietSet.add(p);
      });
      setTrusted(safe); setQuiet(quietSet);
    }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, ME]);

  useEffect(() => { const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300); return () => window.clearTimeout(t); }, [search]);

  // the message list for the open folder (search, filter and sort run on the server)
  useEffect(() => {
    if (!connected || NO_MAIL_FOLDERS.includes(activeFolder)) { setLoading(false); return; }
    const ac = new AbortController();
    setLoading(true); setLoadError(null); pageNo.current = 1;
    fetchPage(1, ac.signal)
      .then((r) => { mergeList(activeFolder, r.messages, false); setHasMore(r.hasMore); setServerOthers(r.othersUnread); setLoading(false); })
      .catch((e) => {
        if (mailApi.isAbort(e)) return;
        setLoading(false);
        setLoadError(e instanceof mailApi.ApiError && (e.status === 401 || e.status === 403 || e.status === 404) ? 'This mailbox can’t be opened with your current access.' : 'Couldn’t load messages. Check your connection.');
      });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, ME, activeFolder, filter, sort, debouncedSearch]);

  // new mail: check every minute while the tab is visible
  useEffect(() => {
    if (!connected) return;
    const t = window.setInterval(() => {
      if (document.hidden) return;
      refreshFoldersNow();
      if (pageNo.current === 1) reloadNow();
    }, 60000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, ME]);

  /* ---------- derived ---------- */
  const inFolder = useMemo(
    () => emails.filter((m) => m.folder === activeFolder && !(m.snoozeUntil && m.snoozeUntil > Date.now())),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emails, activeFolder, tick],
  );
  const splitFocus = activeFolder === 'inbox' && focusedOn;
  const localOthers = splitFocus ? inFolder.filter((m) => !m.focused && !m.isRead).length : 0;
  const othersUnread = splitFocus ? (hasMore ? serverOthers : localOthers) : 0;
  const othersAll = splitFocus ? inFolder.filter((m) => !m.focused) : [];

  const list = useMemo(() => {
    const q = ''; // search runs on the server (API: ?q=)
    const pool = splitFocus ? inFolder.filter((m) => (category === 'focused') === m.focused) : inFolder;
    const out = pool.filter((m) => {
      if (filter === 'unread' && m.isRead) return false;
      if (filter === 'flagged' && !m.isFlagged) return false;
      if (filter === 'attachments' && m.attachments.length === 0) return false;
      if (q && !`${m.subject} ${m.fromName ?? ''} ${m.from} ${m.snippet} ${textOf(m.body)}`.toLowerCase().includes(q)) return false;
      return true;
    });
    const t = (m: Mail) => new Date(m.date).getTime();
    out.sort((a, b) => {
      if (sort === 'dateAsc') return t(a) - t(b);
      if (sort === 'from') return (a.fromName ?? a.from).localeCompare(b.fromName ?? b.from);
      if (sort === 'subject') return a.subject.localeCompare(b.subject);
      return t(b) - t(a);
    });
    return out;
  }, [inFolder, splitFocus, category, filter, search, sort]);

  const groups = useMemo(() => {
    const out: { key: string; items: Mail[] }[] = [];
    const pinned = list.filter((m) => m.pinned);
    const rest = list.filter((m) => !m.pinned);
    if (pinned.length) out.push({ key: 'Pinned', items: pinned });
    if (group && (sort === 'dateDesc' || sort === 'dateAsc')) {
      const map = new Map<string, Mail[]>();
      rest.forEach((m) => { const k = bucketOf(m.date); const a = map.get(k); if (a) a.push(m); else map.set(k, [m]); });
      const order = sort === 'dateDesc' ? BUCKET_ORDER : [...BUCKET_ORDER].reverse();
      order.forEach((k) => { const it = map.get(k); if (it) out.push({ key: k, items: it }); });
    } else if (rest.length) out.push({ key: pinned.length ? 'Other messages' : '', items: rest });
    return out;
  }, [list, group, sort]);

  const flatIds = useMemo(() => groups.filter((g) => !collapsed.has(g.key)).reduce<string[]>((acc, g) => acc.concat(g.items.map((m) => m.id)), []), [groups, collapsed]);

  const selected = useMemo(() => emails.find((m) => m.id === selectedId && m.folder === activeFolder) ?? null, [emails, selectedId, activeFolder]);
  const ids = (checked.size ? Array.from(checked) : selected ? [selected.id] : []).filter((id) => emails.some((m) => m.id === id && m.folder === activeFolder));
  const targets = emails.filter((m) => ids.includes(m.id));
  const primary = targets.length === 1 ? targets[0] : null;

  const contacts = useMemo<Contact[]>(() => {
    const map = new Map<string, string>();
    emails.forEach((m) => {
      if (m.from !== ME) map.set(m.from, m.fromName ?? nameFromAddr(m.from));
      (m.to ?? []).forEach((a, i) => { if (a !== ME && !map.has(a)) map.set(a, m.toNames?.[i] || nameFromAddr(a)); });
    });
    return Array.from(map, ([address, name]) => ({ name, address }));
  }, [emails]);

  const badge = (id: string): { text: string; unread: boolean } | null => {
    const f = apiFolders.find((x) => x.id === id);
    if (!f) return null;
    if (BRACKET_FOLDERS.includes(id)) {
      const n = id === 'junk' ? f.unread : f.total;
      return n ? { text: `[${n}]`, unread: false } : null;
    }
    return f.unread ? { text: String(f.unread), unread: true } : null;
  };

  /* ---------- core mutations ---------- */
  const say = (msg: string, undo?: () => void) => {
    const id = ++toastId.current;
    setToast({ id, msg, undo: undo ?? null });
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast((t) => (t && t.id === id ? null : t)), undo ? 7000 : 3500);
  };
  const setAll = (next: Mail[]) => { emailsRef.current = next; setEmails(next); };
  const failed = (e: unknown) => {
    const code = e instanceof mailApi.ApiError ? e.code : '';
    say(code === 'unauthorized' || code === 'mailbox_not_in_tenant' || code === 'mailbox_not_found' ? 'Your access to this mailbox was refused.' : 'That didn’t go through. Please try again.');
  };
  // Changes the screen at once, tells the server, and puts everything back if the server refuses.
  const commit = (next: Mail[], msg?: string, remote?: Remote) => {
    const prev = emailsRef.current;
    setAll(next);
    if (remote?.api) {
      remote.api().then(() => { refreshFolders(); if (remote.reload) reloadNow(); }).catch((e) => { setAll(prev); failed(e); });
    }
    if (msg) {
      const u = remote?.undo;
      say(msg, u === false ? undefined : () => { setAll(prev); if (u) u().then(refreshFolders).catch(failed); });
    }
  };
  const patch = (idList: string[], fn: (m: Mail) => Mail, msg?: string, remote?: Remote) =>
    commit(emailsRef.current.map((m) => (idList.includes(m.id) ? fn(m) : m)), msg, remote);

  const advance = (idList: string[]) => {
    if (selectedId && idList.includes(selectedId)) {
      const i = flatIds.indexOf(selectedId);
      let next: string | null = null;
      for (let j = i + 1; j < flatIds.length && !next; j++) { const c = flatIds[j]; if (c !== undefined && !idList.includes(c)) next = c; }
      for (let j = i - 1; j >= 0 && !next; j--) { const c = flatIds[j]; if (c !== undefined && !idList.includes(c)) next = c; }
      setSelectedId(next);
      if (next) {
        const nx: string = next;
        patch([nx], (m) => ({ ...m, isRead: true }), undefined, { api: () => mailApi.setRead([nx], true) });
        const nm = emailsRef.current.find((x) => x.id === nx);
        if (nm && !nm.body) loadBody(nx);
      } else setShowReader(false);
    }
    setChecked(new Set());
  };

  const plural = (n: number) => (n > 1 ? `${n} messages` : 'Message');
  const moveTo = (idList: string[], folder: string, text?: string, extra?: Remote) => {
    if (!idList.length) return;
    const origin = new Map(emailsRef.current.filter((m) => idList.includes(m.id)).map((m) => [m.id, m.folder] as const));
    const back = () => {
      const byFolder = new Map<string, string[]>();
      origin.forEach((f, id) => byFolder.set(f, [...(byFolder.get(f) ?? []), id]));
      return Promise.all(Array.from(byFolder, ([f, list2]) => mailApi.moveMessages(list2, f)));
    };
    advance(idList);
    patch(idList, (m) => ({ ...m, folder, pinned: false, snoozeUntil: null }), text ?? `${plural(idList.length)} moved to ${folderLabel(folder)}`,
      { api: () => mailApi.moveMessages(idList, folder), undo: back, ...extra });
  };
  const del = (idList: string[]) => {
    if (!idList.length) return;
    if (activeFolder === 'trash') setConfirm({ kind: 'purge', ids: idList });
    else moveTo(idList, 'trash', `${plural(idList.length)} deleted`);
  };
  const purge = (idList: string[]) => {
    advance(idList);
    commit(emailsRef.current.filter((m) => !idList.includes(m.id)), `${plural(idList.length)} permanently deleted`,
      { api: () => mailApi.deleteMessages(idList), undo: false });
  };
  const archive = (idList: string[]) => {
    if (!idList.length) return;
    if (activeFolder === 'archive') { say('These messages are already in Archive'); return; }
    moveTo(idList, 'archive', `${plural(idList.length)} archived`);
  };
  const toggleRead = (idList: string[]) => {
    const ms = emailsRef.current.filter((m) => idList.includes(m.id));
    const allRead = ms.every((m) => m.isRead);
    patch(idList, (m) => ({ ...m, isRead: !allRead }), undefined, { api: () => mailApi.setRead(idList, !allRead) });
  };
  const setFlag = (idList: string[], on: boolean) =>
    patch(idList, (m) => ({ ...m, isFlagged: on }), undefined, { api: () => mailApi.setFlag(idList, on) });
  const togglePin = (idList: string[]) => {
    const all = emailsRef.current.filter((m) => idList.includes(m.id)).every((m) => m.pinned);
    patch(idList, (m) => ({ ...m, pinned: !all }), all ? 'Unpinned' : 'Pinned to the top', { api: () => mailApi.setPin(idList, !all), undo: () => mailApi.setPin(idList, all) });
  };
  const toggleCategory = (idList: string[], cat: string) => {
    const all = emailsRef.current.filter((m) => idList.includes(m.id)).every((m) => m.categories.includes(cat));
    patch(idList, (m) => ({ ...m, categories: all ? m.categories.filter((c) => c !== cat) : m.categories.includes(cat) ? m.categories : [...m.categories, cat] }),
      undefined, { api: () => mailApi.setCategory(idList, cat, !all) });
  };
  const snooze = (idList: string[], kind: string) => {
    const d = new Date();
    if (kind === 'later') d.setHours(d.getHours() + 3);
    else if (kind === 'tomorrow') { d.setDate(d.getDate() + 1); d.setHours(8, 0, 0, 0); }
    else if (kind === 'weekend') { d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7 || 7)); d.setHours(8, 0, 0, 0); }
    else { d.setDate(d.getDate() + (((8 - d.getDay()) % 7) || 7)); d.setHours(8, 0, 0, 0); }
    advance(idList);
    patch(idList, (m) => ({ ...m, snoozeUntil: d.getTime() }),
      `Snoozed until ${d.toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' })}`,
      { api: () => mailApi.snooze(idList, d.getTime()), undo: () => mailApi.snooze(idList, null) });
  };
  const fromSender = (sender: string) => emailsRef.current.filter((m) => m.from === sender && m.folder === activeFolder).map((m) => m.id);

  const blockSender = (m: Mail) => {
    const all = emailsRef.current.filter((x) => x.from === m.from && x.folder !== 'sent' && x.folder !== 'drafts').map((x) => x.id);
    advance(all);
    patch(all, (x) => ({ ...x, folder: 'junk', pinned: false }), `Blocked ${m.fromName ?? m.from}. Their messages now go to Junk Email.`,
      { api: () => mailApi.blockSender(m.from), undo: false, reload: true });
  };
  const ignoreThread = (m: Mail) => {
    const key = normSubject(m.subject);
    const all = emailsRef.current.filter((x) => x.folder === activeFolder && normSubject(x.subject) === key).map((x) => x.id);
    moveTo(all, 'trash', 'Conversation ignored and moved to Deleted Items', { api: () => mailApi.ignoreConversation(m.id), reload: true });
  };
  const runSweep = (sender: string) => {
    let victims = emailsRef.current.filter((m) => m.from === sender && m.folder === activeFolder);
    if (sweepMode === 'old') victims = victims.filter((m) => Date.now() - new Date(m.date).getTime() > 10 * 86400000);
    if (sweepMode === 'keep') {
      let newestId = '';
      let newestAt = 0;
      victims.forEach((m) => { const t = new Date(m.date).getTime(); if (t > newestAt) { newestAt = t; newestId = m.id; } });
      victims = victims.filter((m) => m.id !== newestId);
    }
    if (!victims.length) { say('Nothing to sweep for that sender'); return; }
    moveTo(victims.map((m) => m.id), 'trash', `${victims.length} message${victims.length > 1 ? 's' : ''} swept to Deleted Items`,
      { api: () => mailApi.sweep(activeFolder, sender, sweepMode as 'all' | 'old' | 'keep'), reload: true });
  };
  const addRule = (m: Mail, k: 'archive' | 'junk' | 'delete') => {
    const dest = k === 'archive' ? 'archive' : k === 'junk' ? 'junk' : 'trash';
    const all = fromSender(m.from);
    advance(all);
    patch(all, (x) => ({ ...x, folder: dest, pinned: false }), `Rule created: messages from ${m.fromName ?? m.from} go to ${folderLabel(dest)}`,
      { api: () => mailApi.addRule(activeFolder, m.from, k), undo: false, reload: true });
  };

  /* ---------- compose ---------- */
  const ensureBody = async (m: Mail): Promise<Mail> => {
    if (m.body) return m;
    try {
      const f = await mailApi.getMessage(m.id);
      return { ...m, body: f.body, attachments: f.attachments as Att[], to: f.to, toNames: f.toNames, cc: f.cc, ccNames: f.ccNames };
    } catch { return m; }
  };
  const openCompose = async (mode: Draft['mode'], srcIn?: Mail) => {
    const src = srcIn ? await ensureBody(srcIn) : undefined;
    let d: Draft = { key: nid(), draftId: null, mode, to: [], cc: [], bcc: [], subject: '', html: SIG, attachments: [] };
    if (src) {
      const pre = (re: RegExp, p: string) => (re.test(src.subject) ? src.subject : `${p}: ${src.subject}`);
      const toAddr = src.from === ME ? [...(src.to ?? [])] : [src.from];
      if (mode === 'reply') d = { ...d, to: toAddr, subject: pre(/^re:/i, 'Re'), html: SIG + quoteOf(src), sourceId: src.id };
      else if (mode === 'replyAll') d = { ...d, to: Array.from(new Set([...toAddr, ...(src.to ?? []).filter((x) => x !== ME)])), cc: (src.cc ?? []).filter((x) => x !== ME), subject: pre(/^re:/i, 'Re'), html: SIG + quoteOf(src), sourceId: src.id };
      else if (mode === 'forward') d = { ...d, subject: pre(/^(fw|fwd):/i, 'Fw'), html: SIG + quoteOf(src), attachments: [...src.attachments], sourceId: src.id, attachFrom: src.id };
      else if (mode === 'meeting') d = { ...d, to: toAddr, subject: `Meeting: ${src.subject.replace(/^((re|fw|fwd):\s*)+/i, '')}` };
    }
    setCompose(d);
    setShowReader(true);
  };
  // API: GET /api/webmail/drafts/{id}
  const openDraft = async (m: Mail) => {
    setSelectedId(m.id);
    setShowReader(true);
    try {
      const d = await mailApi.getDraft(m.id);
      setCompose({ key: nid(), draftId: d.draftId, mode: 'new', to: d.to, cc: d.cc, bcc: d.bcc, subject: d.subject, html: d.html, attachments: d.attachments as Att[], attachFrom: d.draftId });
    } catch (e) { failed(e); }
  };
  const buildMail = (d: Draft, folder: string, id: string): Mail => ({
    id, folder, subject: d.subject, from: ME, fromName: ME_NAME,
    to: d.to, toNames: d.to.map((a) => contacts.find((c) => c.address === a)?.name ?? nameFromAddr(a)),
    cc: d.cc, ccNames: d.cc.map((a) => contacts.find((c) => c.address === a)?.name ?? nameFromAddr(a)),
    date: new Date().toISOString(), snippet: textOf(d.html).slice(0, 120), body: d.html,
    isRead: true, isFlagged: false, attachments: d.attachments.map(({ file: _f, ...rest }) => rest), pinned: false, external: false, focused: true, categories: [], snoozeUntil: null,
  });
  const toPayload = async (d: Draft): Promise<mailApi.ComposePayload> => {
    const kept = d.attachments.filter((x) => x.id && !x.file);
    const fresh = d.attachments.filter((x) => x.file);
    return {
      draftId: d.draftId, mode: d.mode === 'meeting' ? 'new' : d.mode, to: d.to, cc: d.cc, bcc: d.bcc, subject: d.subject, html: d.html,
      inReplyTo: d.sourceId ?? null,
      attachmentsFrom: kept.length ? (d.attachFrom ?? d.sourceId ?? d.draftId) : null,
      keepAttachmentIds: kept.map((x) => x.id as string),
      attachments: await Promise.all(fresh.map(async (x) => ({
        name: x.name, ...(x.type && x.type !== 'file' ? { contentType: x.type } : {}), contentBase64: await mailApi.fileToBase64(x.file as File),
      }))),
    };
  };
  // API: POST /api/webmail/drafts
  const saveDraft = async (d: Draft, quiet = false): Promise<string | null> => {
    try {
      const r = await mailApi.saveDraftApi(await toPayload(d));
      const m = buildMail(d, 'drafts', r.draftId);
      const gone = d.draftId && d.draftId !== r.draftId ? d.draftId : null;
      const cur = emailsRef.current.filter((x) => x.id !== gone);
      setAll(cur.some((x) => x.id === r.draftId) ? cur.map((x) => (x.id === r.draftId ? m : x)) : [m, ...cur]);
      setCompose((c) => (c && c.key === d.key ? { ...c, draftId: r.draftId } : c));
      if (!quiet) say('Draft saved');
      refreshFolders();
      return r.draftId;
    } catch (e) {
      if (!quiet) failed(e);
      return null;
    }
  };
  const autoSave = () => {
    const d = composeGetter.current?.();
    if (d && (d.to.length || d.subject.trim())) void saveDraft(d, true);
  };
  // API: DELETE /api/webmail/drafts/{id}
  const discard = (d: Draft) => {
    if (d.draftId) {
      const id = d.draftId;
      setAll(emailsRef.current.filter((m) => m.id !== id));
      mailApi.discardDraft(id).then(refreshFolders).catch(() => undefined);
    }
    pendingDelete.current = null;
    setCompose(null);
    setShowReader(false);
    say('Draft discarded');
  };
  // API: POST /api/webmail/send  (the message is queued for delivery straight away, so there is no undo)
  const doSend = async (d: Draft) => {
    if (sending.current) return;
    sending.current = true;
    say('Sending…');
    try {
      await mailApi.sendMail(await toPayload(d));
      const pd = pendingDelete.current;
      pendingDelete.current = null;
      let next = emailsRef.current.filter((m) => m.id !== d.draftId);
      if (pd) {
        next = next.map((m) => (m.id === pd ? { ...m, folder: 'trash', pinned: false } : m));
        mailApi.moveMessages([pd], 'trash').catch(() => undefined);
      }
      setAll(next);
      setCompose(null);
      setShowReader(false);
      say('Message sent');
      refreshFolders();
      if (P.current.activeFolder === 'sent' || P.current.activeFolder === 'drafts') reloadNow();
    } catch (e) {
      const code = e instanceof mailApi.ApiError ? e.code : '';
      say(code === 'attachments_too_large' ? 'Attachments are too large (25 MB limit).' : 'Couldn’t send the message. It is still open, so you can try again.');
    } finally { sending.current = false; }
  };
  const trySend = (d: Draft): string | null => {
    if (!d.subject.trim()) { pendingSend.current = d; setConfirm({ kind: 'nosubject' }); return null; }
    void doSend(d);
    return null;
  };

  /* ---------- selection / navigation ---------- */
  const leaveCompose = () => { if (compose) { autoSave(); setCompose(null); } };
  const selectMail = (m: Mail) => {
    leaveCompose();
    if (m.folder === 'drafts') { void openDraft(m); return; }
    setSelectedId(m.id);
    setShowReader(true);
    setSummaryFor(null);
    if (!m.isRead) patch([m.id], (x) => ({ ...x, isRead: true }), undefined, { api: () => mailApi.setRead([m.id], true) });
    if (!m.body) loadBody(m.id); // API: GET /api/webmail/messages/{id}
  };
  const goFolder = (id: string) => {
    leaveCompose();
    setActiveFolder(id);
    setChecked(new Set());
    setSelectedId(null);
    setSearch('');
    setSearchOpen(false);
    setFilter('all');
    setShowReader(false);
    setCategory('focused');
    if (typeof window !== 'undefined' && window.innerWidth <= 900) setNav(false);
  };
  const step = (delta: number) => {
    if (!flatIds.length) return;
    const i = selectedId ? flatIds.indexOf(selectedId) : -1;
    const nextId = flatIds[Math.min(flatIds.length - 1, Math.max(0, i + delta))];
    const m = emails.find((x) => x.id === nextId);
    if (m && !compose) selectMail(m);
  };
  const checkOne = (id: string, on: boolean) => setChecked((p) => { const n = new Set(p); if (on) n.add(id); else n.delete(id); return n; });
  const allChecked = list.length > 0 && list.every((m) => checked.has(m.id));

  // API: POST /api/webmail/folders
  const createFolder = async () => {
    const name = folderName.trim();
    if (!name) { setAddingFolder(false); return; }
    if (creatingFolder.current) return;
    if (folders.some((f) => f.label.toLowerCase() === name.toLowerCase())) { say('A folder with that name already exists'); return; }
    creatingFolder.current = true;
    setFolderName(''); setAddingFolder(false);
    try {
      await mailApi.createFolder(name);
      refreshFoldersNow();
      say(`Folder “${name}” created`);
    } catch (e) {
      say(e instanceof mailApi.ApiError && e.code === 'exists' ? 'A folder with that name already exists' : 'Couldn’t create the folder. Try a different name.');
    } finally { creatingFolder.current = false; }
  };
  const startNewFolder = () => { setNav(true); setOpenItems((p) => (p.includes('account-0') ? p : [...p, 'account-0'])); setAddingFolder(true); };

  const quick = (k: 'done' | 'replydelete' | 'flagread') => {
    if (!ids.length) return;
    const L = [...ids];
    const from = activeFolder;
    if (k === 'done') {
      advance(L);
      patch(L, (m) => ({ ...m, isRead: true, folder: 'archive', pinned: false }), `${plural(L.length)} marked read and archived`,
        { api: async () => { await mailApi.setRead(L, true); await mailApi.archiveMessages(L); }, undo: () => mailApi.moveMessages(L, from) });
    } else if (k === 'flagread') {
      patch(L, (m) => ({ ...m, isFlagged: true, isRead: true }), 'Flagged and marked as read',
        { api: () => Promise.all([mailApi.setFlag(L, true), mailApi.setRead(L, true)]), undo: false });
    } else if (primary) { pendingDelete.current = primary.id; void openCompose('reply', primary); }
  };

  const a: Actions = {
    newMail: () => { leaveCompose(); openCompose('new'); },
    del: () => del(ids), archive: () => archive(ids),
    ignore: () => primary && ignoreThread(primary), block: () => primary && blockSender(primary),
    report: (k) => {
      if (k === 'notjunk') moveTo(ids, 'inbox', 'Moved to Inbox');
      else moveTo(ids, 'junk', k === 'phishing' ? 'Reported as phishing and moved to Junk Email' : 'Reported as junk');
    },
    reply: () => primary && openCompose('reply', primary),
    replyAll: () => primary && openCompose('replyAll', primary),
    forward: () => primary && openCompose('forward', primary),
    meeting: () => primary && openCompose('meeting', primary),
    moveTo: (id) => moveTo(ids, id), newFolder: startNewFolder,
    sweep: () => primary && setConfirm({ kind: 'sweep', sender: primary.from, senderName: primary.fromName ?? primary.from }),
    rule: (k) => primary && addRule(primary, k),
    quick, toggleRead: () => toggleRead(ids), flag: (on) => setFlag(ids, on), pin: () => togglePin(ids),
    category: (id) => toggleCategory(ids, id), snooze: (k) => snooze(ids, k),
    print: () => primary && printMail(primary, sanitize(primary.body, allowRemoteFor(primary)).html),
    saveAs: () => { if (primary) mailApi.downloadEml(primary.id).catch(() => say('Couldn’t download the message.')); }, // API: GET .../messages/{id}/raw
    shortcuts: () => setConfirm({ kind: 'shortcuts' }),
  };

  // Reader actions always act on the open message
  const ra: Actions = selected ? {
    ...a,
    reply: () => openCompose('reply', selected), replyAll: () => openCompose('replyAll', selected), forward: () => openCompose('forward', selected),
    archive: () => archive([selected.id]), del: () => del([selected.id]),
    report: (k) => (k === 'notjunk' ? moveTo([selected.id], 'inbox', 'Moved to Inbox') : moveTo([selected.id], 'junk', 'Reported as junk')),
    print: () => printMail(selected, sanitize(selected.body, allowRemoteFor(selected)).html),
    saveAs: () => { mailApi.downloadEml(selected.id).catch(() => say('Couldn’t download the message.')); },
  } : a;

  // API: POST /api/webmail/folders/{id}/empty
  const emptyFolder = (id: string) => {
    const all = emailsRef.current.filter((m) => m.folder === id).map((m) => m.id);
    advance(all);
    commit(emailsRef.current.filter((m) => m.folder !== id), `${folderLabel(id)} emptied`, { api: () => mailApi.emptyFolder(id), undo: false });
  };

  // remote images: swap in the proxied copy (sender never sees the reader's address) before showing them
  useEffect(() => {
    const m = selected;
    if (!m || !m.body || proxying.current.has(m.id)) return;
    if (!(isTrusted(m.from) || shown.has(m.id))) return;
    proxying.current.add(m.id);
    mailApi.unblockImages(m.id) // API: POST .../messages/{id}/unblock
      .then((r) => {
        setAll(emailsRef.current.map((x) => (x.id === m.id ? { ...x, body: r.body } : x)));
        setProxiedIds((p) => new Set(p).add(m.id));
      })
      .catch(() => { proxying.current.delete(m.id); say('Couldn’t load the blocked content.'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, selected?.body, shown, trusted]);

  /* ---------- keyboard ---------- */
  keyRef.current = (e: KeyboardEvent) => {
    const t = e.target as HTMLElement | null;
    if (t && t.closest('input,textarea,select,[contenteditable="true"],[role="dialog"],[role="menu"]')) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (compose) return;
    const k = e.key;
    if (k === 'n') { e.preventDefault(); a.newMail(); }
    else if (k === 'r' && primary) { e.preventDefault(); a.reply(); }
    else if (k === 'R' && primary) { e.preventDefault(); a.replyAll(); }
    else if (k === 'f' && primary) { e.preventDefault(); a.forward(); }
    else if (k === 'Delete' && ids.length) { e.preventDefault(); a.del(); }
    else if (k === 'e' && ids.length) { e.preventDefault(); a.archive(); }
    else if (k === 'u' && ids.length) { e.preventDefault(); a.toggleRead(); }
    else if (k === 'q' && ids.length) { e.preventDefault(); setFlag(ids, !targets.every((m) => m.isFlagged)); }
    else if (k === 'j' || k === 'ArrowDown') { e.preventDefault(); step(1); }
    else if (k === 'k' || k === 'ArrowUp') { e.preventDefault(); step(-1); }
    else if (k === '/') { e.preventDefault(); setSearchOpen(true); window.setTimeout(() => document.querySelector<HTMLInputElement>(`.${styles.oxSearch} input`)?.focus(), 30); }
    else if (k === 'Escape') { if (checked.size) setChecked(new Set()); else if (search || searchOpen) { setSearch(''); setSearchOpen(false); } }
  };
  useEffect(() => {
    const h = (e: KeyboardEvent) => keyRef.current(e);
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  /* ---------- render helpers ---------- */
  const folderRow = (id: string, custom = false, fav = false) => {
    const f = folders.find((x) => x.id === id);
    if (!f) return null;
    const b = badge(id);
    return (
      <div
        key={id}
        role="button"
        tabIndex={0}
        className={`${styles.folderRow} ${styles.oxFolder} ${activeFolder === id && !fav ? styles.folderActive : ''} ${dropFolder === id ? styles.oxDrop : ''}`}
        onClick={() => goFolder(id)}
        onKeyDown={(e) => { if (e.key === 'Enter') goFolder(id); }}
        onDragOver={(e) => { if (!NO_MAIL_FOLDERS.includes(id)) { e.preventDefault(); setDropFolder(id); } }}
        onDragLeave={() => setDropFolder((d) => (d === id ? null : d))}
        onDrop={(e) => {
          e.preventDefault(); setDropFolder(null);
          const mid = e.dataTransfer.getData('text/mail-id');
          if (mid && !NO_MAIL_FOLDERS.includes(id)) moveTo(checked.has(mid) ? Array.from(checked) : [mid], id);
        }}
      >
        <span className={styles.folderIcon}><FolderGlyph id={id} /></span>
        <span className={styles.folderName}>{f.label}</span>
        {custom && (
          <Tooltip content="Delete folder" relationship="label">
            <button type="button" className={styles.oxFolderX} aria-label={`Delete folder ${f.label}`} onClick={(e) => { e.stopPropagation(); setConfirm({ kind: 'deleteFolder', id }); }}><Dismiss16Regular /></button>
          </Tooltip>
        )}
        {b && <span className={`${styles.folderCount} ${b.unread ? styles.oxCountUnread : ''}`}>{b.text}</span>}
      </div>
    );
  };

  const rowFor = (m: Mail) => (
    <MailItem
      key={m.id} mail={m} folder={activeFolder}
      isChecked={checked.has(m.id)} anyChecked={checked.size > 0} isSelected={selected?.id === m.id} preview={preview}
      onCheckChange={checkOne}
      onFlagChange={(id, f) => setFlag([id], f)}
      onPinChange={(id) => togglePin([id])}
      onDelete={(id) => del([id])}
      onSelect={selectMail}
      onToggleRead={(id) => toggleRead([id])}
      onArchive={(id) => archive([id])}
    />
  );

  const noHits = list.length === 0;
  const nothingMsg = !connected ? 'Select a mailbox to load your mail.' : loading ? 'Loading…' : loadError ? loadError : search || filter !== 'all' ? 'No messages match your search or filter.' : activeFolder === 'notes' || activeFolder === 'conversation' || activeFolder === 'rss' || activeFolder === 'search' ? `${folderLabel(activeFolder)} isn’t connected yet.` : `Nothing in ${folderLabel(activeFolder)}.`;
  const subjectTitle = compose ? (compose.mode === 'new' ? 'New message' : compose.mode === 'forward' ? 'Forward' : compose.mode === 'meeting' ? 'New meeting message' : 'Reply') : selected ? (selected.subject || '(No subject)') : 'Select an email';
  const dlgCount = confirm?.kind === 'purge' ? confirm.ids.length : 0;

  return (
    <div className={`${styles.outlookRoot} ${styles.oxRoot} ${nav ? styles.oxNavOn : styles.oxNavOff} ${showReader ? styles.oxReaderOpen : ''} ${compact ? styles.oxCompact : ''}`}>
      <div className={styles.UVgTR}>
        <div className={styles.HJBsNWT4D}>
          <div className={styles.Cr217}>
            <Ribbon
              has={ids.length > 0} hasOne={!!primary}
              readIs={targets.length > 0 && targets.every((m) => m.isRead) ? 'read' : 'unread'}
              pinned={targets.length > 0 && targets.every((m) => m.pinned)}
              flagged={targets.length > 0 && targets.every((m) => m.isFlagged)}
              folders={folders.filter((f) => f.id !== activeFolder && !NO_MAIL_FOLDERS.includes(f.id) && f.id !== 'drafts' && f.id !== 'sent')}
              inJunk={activeFolder === 'junk'}
              catOn={(id) => targets.length > 0 && targets.every((m) => m.categories.includes(id))}
              a={a}
              v={{ nav, setNav, pane, setPane, preview, setPreview, group, setGroup, focusedOn, setFocusedOn, compact, setCompact, sort, setSort }}
            />
          </div>
        </div>
      </div>

      <div className={styles.threePane}>
        {/* Left nav */}
        <div className={styles.leftNav}>
          <Accordion openItems={openItems} onToggle={(_, d) => setOpenItems(d.openItems as string[])} multiple collapsible className={styles.folderAccordion}>
            <AccordionItem value="favorites">
              <AccordionHeader expandIcon={<ChevronDown16Regular />}><span className={styles.navSectionTitle}>Favorites</span></AccordionHeader>
              <AccordionPanel>{FAVORITES.map((id) => folderRow(id, false, true))}</AccordionPanel>
            </AccordionItem>
            <AccordionItem value="account-0">
              <AccordionHeader expandIcon={<ChevronDown16Regular />}><span className={styles.navSectionTitle} title={ME}>{ME}</span></AccordionHeader>
              <AccordionPanel>
                {SYSTEM_FOLDERS.map((f) => folderRow(f.id))}
                {customFolders.map((f) => folderRow(f.id, true))}
                {addingFolder ? (
                  <div className={styles.oxNewFolder}>
                    <Folder16Regular />
                    <input autoFocus value={folderName} aria-label="Folder name" placeholder="Folder name" maxLength={40}
                      onChange={(e) => setFolderName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') { setAddingFolder(false); setFolderName(''); } }}
                      onBlur={createFolder} />
                  </div>
                ) : (
                  <button type="button" className={styles.oxAddFolder} onClick={startNewFolder}><FolderAdd16Regular />New folder</button>
                )}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
          <div className={styles.oxNavFoot}>
          <Button appearance="subtle" size="small" icon={<PeopleCommunity16Regular />} className={styles.oxNavBtn} onClick={() => say('Groups will open here once your directory API is connected')}>
            Go to Groups
          </Button>
          <Button appearance="subtle" size="small" icon={<Add16Regular />} className={`${styles.addAccountBtn} ${styles.oxNavBtn}`} onClick={() => say('Connect your mail provider here once the mail API is ready')}>
            Add email account
          </Button>
          </div>
        </div>
        <div className={styles.oxScrim} onClick={() => setNav(false)} />

        {/* Middle + Right */}
        <div className={`${styles.nbmyu} ${styles.FkPdL} ${styles.LCprM} ${styles.oxMain} ${pane === 'bottom' ? styles.oxPaneBottom : ''} ${pane === 'off' ? styles.oxPaneOff : ''}`}>
          {/* Message list */}
          <div className={`${styles.rRhzD} ${styles.css182}`}>
            <div className={`${styles.middlePane} ${styles.C1EJK} ${styles.LyMTI}`}>
              <div className={styles.listHeader}>
                {searchOpen ? (
                  <Input className={styles.oxSearch} size="small" autoFocus placeholder={`Search ${folderLabel(activeFolder)}`} value={search} contentBefore={<Search16Regular />}
                    contentAfter={<Dismiss16Regular className={styles.oxClear} onClick={() => { setSearch(''); setSearchOpen(false); }} />}
                    onChange={(_, d) => setSearch(d.value)} />
                ) : (
                  <>
                    {splitFocus ? (
                      <div className={styles.focusedTabs}>
                        <button className={`${styles.tabBtn} ${category === 'focused' ? styles.tabActive : ''}`} onClick={() => setCategory('focused')}>Focused</button>
                        <button className={`${styles.tabBtn} ${category === 'other' ? styles.tabActive : ''}`} onClick={() => setCategory('other')}>Other{othersUnread > 0 && <i className={styles.oxTabBadge}>{othersUnread}</i>}</button>
                      </div>
                    ) : <div className={styles.oxListTitle}>{folderLabel(activeFolder)}</div>}
                    <div className={styles.oxListTools}>
                      {(activeFolder === 'trash' || activeFolder === 'junk') && inFolder.length > 0 && (
                        <Button appearance="subtle" size="small" onClick={() => setConfirm({ kind: 'empty', folder: activeFolder })}>Empty folder</Button>
                      )}
                      <Button appearance="subtle" size="small" className={search ? styles.oxToolOn : ''} icon={<Search16Regular />} title="Search (/)" aria-label="Search" onClick={() => setSearchOpen(true)} />
                      <Button appearance="subtle" size="small" icon={<SelectAllOn16Regular />} title={allChecked ? 'Clear selection' : 'Select all'} aria-label="Select all" onClick={() => setChecked(allChecked ? new Set() : new Set(list.map((m) => m.id)))} disabled={noHits} />
                      <Menu>
                        <MenuTrigger disableButtonEnhancement>
                          <Button appearance="subtle" size="small" className={filter !== 'all' ? styles.oxToolOn : ''} icon={<Filter16Regular />} aria-label="Filter" title="Filter" />
                        </MenuTrigger>
                        <MenuPopover><MenuList>
                          {([['all', 'All'], ['unread', 'Unread'], ['flagged', 'Flagged'], ['attachments', 'Has attachments']] as [FilterKey, string][]).map(([k, l]) => (
                            <MenuItem key={k} icon={filter === k ? <Checkmark16Regular /> : null} onClick={() => setFilter(k)}>{l}</MenuItem>
                          ))}
                        </MenuList></MenuPopover>
                      </Menu>
                      <Menu>
                        <MenuTrigger disableButtonEnhancement><Button appearance="subtle" size="small" icon={<ArrowSort16Regular />} aria-label="Sort" title="Sort" /></MenuTrigger>
                        <MenuPopover><MenuList>
                          {([['dateDesc', 'Date: newest on top'], ['dateAsc', 'Date: oldest on top'], ['from', 'From'], ['subject', 'Subject']] as [SortKey, string][]).map(([k, l]) => (
                            <MenuItem key={k} icon={sort === k ? <Checkmark16Regular /> : null} onClick={() => setSort(k)}>{l}</MenuItem>
                          ))}
                        </MenuList></MenuPopover>
                      </Menu>
                    </div>
                  </>
                )}
              </div>

              {checked.size > 0 ? (
                <div className={styles.oxSelBar}>
                  <b>{checked.size} selected</b>
                  <span className={styles.oxGrow} />
                  <Button appearance="subtle" size="small" icon={<Delete16Regular />} onClick={a.del}>Delete</Button>
                  <Button appearance="subtle" size="small" icon={<Archive16Regular />} onClick={a.archive}>Archive</Button>
                  <Button appearance="subtle" size="small" icon={<MailRead16Regular />} onClick={a.toggleRead}>Read</Button>
                  <Button appearance="subtle" size="small" icon={<Dismiss16Regular />} aria-label="Clear selection" onClick={() => setChecked(new Set())} />
                </div>
              ) : null}

              {splitFocus && category === 'focused' && othersUnread > 0 && !search && (
                <button type="button" className={`${styles.otherEmailsHeader} ${styles.oxOther}`} onClick={() => setCategory('other')}>
                  <span className={styles.oxOtherIcon}><Mail16Regular /></span>
                  <span className={styles.oxOtherText}>
                    <b>Other Emails ({othersUnread})</b>
                    <em>{Array.from(new Set(othersAll.map((m) => m.fromName ?? m.from))).slice(0, 4).join('; ')}</em>
                  </span>
                </button>
              )}

              <div className={styles.messageList} role="listbox" aria-label="Messages">
                {groups.map((g) => (
                  <div key={g.key || 'all'}>
                    {g.key && (
                      <button type="button" className={`${styles.monthHeader} ${styles.oxGroupHead}`} aria-expanded={!collapsed.has(g.key)}
                        onClick={() => setCollapsed((p) => { const n = new Set(p); if (n.has(g.key)) n.delete(g.key); else n.add(g.key); return n; })}>
                        {collapsed.has(g.key) ? <ChevronRight16Regular /> : <ChevronDown16Regular />}
                        <span>{g.key}</span>
                      </button>
                    )}
                    {!collapsed.has(g.key) && g.items.map(rowFor)}
                  </div>
                ))}
                {hasMore && !loading && (
                  <div style={{ padding: 8, textAlign: 'center' }}><Button size="small" appearance="subtle" onClick={loadMore}>Load more</Button></div>
                )}
                {noHits && (
                  <div className={styles.oxEmpty}>
                    <MailInbox16Regular />
                    <p>{nothingMsg}</p>
                    {!loading && !loadError && (search || filter !== 'all') && <Button size="small" onClick={() => { setSearch(''); setFilter('all'); }}>Clear search and filter</Button>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Reading pane */}
          <div className={`${styles.Mq3cC} ${styles.cssI184}`}>
            <div className={styles.Xsklh}>
              <div className={styles.g_zET}>
                <div className={styles.Pzn2X}>
                  <div className={styles.KD9CV}>
                    <div className={styles.Q8TCC}>
                      <div className={`${styles.ubHDG} ${styles.gZ9KR} ${styles.THa4i} ${styles.pinIo}`}>
                        <div className={`${styles.l9eF1} ${styles.mJflQ} ${styles.allowTextSelection}`}>
                          <div className={`${styles.MshDW} ${styles.qmv6a}`}>
                            <div className={`${styles.UUCdJ} ${styles.F0Tdc}`}>
                              <div className={styles.HlO3X}>
                                <Button appearance="subtle" size="small" className={styles.oxBack} icon={<ChevronLeft16Regular />} aria-label="Back to messages" onClick={() => setShowReader(false)} />
                                <span className={styles.JdFsz} title={subjectTitle}>{subjectTitle}</span>
                                {selected && !compose && (
                                  <div className={styles.ecmaX}>
                                    <Button icon={<NotepadSparkle16Regular />} shape="circular" size="small" type="button" className={styles.oxSumBtn}
                                      onClick={() => setSummaryFor(summaryFor === selected.id ? null : selected.id)}>
                                      Summarize this email
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className={`${styles.rightPane} ${styles.Vm3e5} ${styles.pinIo}`}>
                        {compose ? (
                          <Compose key={compose.key} draft={compose} contacts={contacts} onSend={trySend} onSave={(d) => saveDraft(d)} onDiscard={discard} getRef={composeGetter} />
                        ) : selected ? (
                          <Reader
                            mail={selected}
                            allowRemote={allowRemoteFor(selected)}
                            summary={summaryFor === selected.id ? summarize(selected) : null}
                            onCloseSummary={() => setSummaryFor(null)}
                            a={ra}
                            onBlock={() => blockSender(selected)}
                            warnOff={isQuiet(selected.from) || isTrusted(selected.from)}
                            onDismissWarn={() => { setQuiet((p) => new Set(p).add(selected.from)); mailApi.setSenderList(selected.from, 'Quiet', true).catch(() => undefined); }}
                            onTrust={() => { setTrusted((p) => new Set(p).add(selected.from)); mailApi.setSenderList(selected.from, 'Safe', true).catch(() => say('Couldn’t save the safe sender.')); say(`${selected.from} added to Safe senders`); }}
                            onShowContent={() => setShown((p) => new Set(p).add(selected.id))}
                            onDownload={(at) => { if (at.id) mailApi.downloadAttachment(selected.id, at.id, at.name).catch(() => say(`Couldn’t download “${at.name}”.`)); }}
                            onToggleRead={() => toggleRead([selected.id])}
                            onFlag={() => setFlag([selected.id], !selected.isFlagged)}
                            onPin={() => togglePin([selected.id])}
                          />
                        ) : (
                          <div className={styles.noSelection}>
                            <MailInbox16Regular style={{ fontSize: 48, opacity: 0.25 }} />
                            <p>{checked.size > 1 ? `${checked.size} items selected` : 'Select an item to read'}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <div className={styles.oxToast} role="status" aria-live="polite">
          <span>{toast.msg}</span>
          {toast.undo && <button type="button" onClick={() => { toast.undo?.(); setToast(null); }}>Undo</button>}
          <button type="button" aria-label="Dismiss" onClick={() => setToast(null)}><Dismiss16Regular /></button>
        </div>
      )}

      <Dialog open={!!confirm} onOpenChange={(_, d) => { if (!d.open) setConfirm(null); }}>
        <DialogSurface className={styles.oxDlg}>
          <DialogBody>
            <DialogTitle>
              {confirm?.kind === 'purge' && 'Delete permanently?'}
              {confirm?.kind === 'empty' && `Empty ${folderLabel(confirm.folder)}?`}
              {confirm?.kind === 'sweep' && 'Sweep messages'}
              {confirm?.kind === 'nosubject' && 'Send without a subject?'}
              {confirm?.kind === 'deleteFolder' && 'Delete folder?'}
              {confirm?.kind === 'shortcuts' && 'Keyboard shortcuts'}
            </DialogTitle>
            <DialogContent>
              {confirm?.kind === 'purge' && `${dlgCount > 1 ? `These ${dlgCount} messages` : 'This message'} will be deleted for good.`}
              {confirm?.kind === 'empty' && `Every message in ${folderLabel(confirm.folder)} will be deleted for good.`}
              {confirm?.kind === 'sweep' && (
                <RadioGroup value={sweepMode} onChange={(_, d) => setSweepMode(d.value)}>
                  <Radio value="all" label={`Delete all messages from ${confirm.senderName}`} />
                  <Radio value="old" label="Delete messages older than 10 days" />
                  <Radio value="keep" label="Keep only the latest message" />
                </RadioGroup>
              )}
              {confirm?.kind === 'nosubject' && 'This message has no subject. Send it anyway?'}
              {confirm?.kind === 'deleteFolder' && `“${folderLabel(confirm.id)}” will be removed and its messages moved to Deleted Items.`}
              {confirm?.kind === 'shortcuts' && (
                <table className={styles.oxKeys}><tbody>{SHORTCUTS.map(([k, l]) => <tr key={k}><td><kbd>{k}</kbd></td><td>{l}</td></tr>)}</tbody></table>
              )}
            </DialogContent>
            <DialogActions>
              {confirm?.kind === 'shortcuts' ? (
                <Button size="small" appearance="primary" onClick={() => setConfirm(null)}>Close</Button>
              ) : (
                <>
                  <Button size="small" onClick={() => setConfirm(null)}>{confirm?.kind === 'nosubject' ? 'Don’t send' : 'Cancel'}</Button>
                  <Button size="small" appearance="primary" onClick={() => {
                    const c = confirm;
                    setConfirm(null);
                    if (!c) return;
                    if (c.kind === 'purge') purge(c.ids);
                    else if (c.kind === 'empty') emptyFolder(c.folder);
                    else if (c.kind === 'sweep') runSweep(c.sender);
                    else if (c.kind === 'nosubject') { if (pendingSend.current) doSend(pendingSend.current); pendingSend.current = null; }
                    else if (c.kind === 'deleteFolder') {
                      // API: DELETE /api/webmail/folders/{id}  (its mail moves to Deleted Items)
                      mailApi.deleteFolder(c.id).then(() => {
                        const mine = emailsRef.current.filter((m) => m.folder === c.id).map((m) => m.id);
                        setAll(emailsRef.current.map((m) => (mine.includes(m.id) ? { ...m, folder: 'trash' } : m)));
                        if (activeFolder === c.id) goFolder('inbox');
                        refreshFoldersNow();
                        say('Folder deleted');
                      }).catch(() => say('Couldn’t delete the folder.'));
                    }
                  }}>
                    {confirm?.kind === 'nosubject' ? 'Send anyway' : confirm?.kind === 'sweep' ? 'OK' : 'Delete'}
                  </Button>
                </>
              )}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

/* =====================================================================
   Email (page): signs the user in to the mail API the same way the old page did.
   - mailbox  = the logged-in user's Cilpron address
   - API key  = the user's tenant key from global.cilpron.com (kept in localStorage)
   ===================================================================== */
const GLOBAL_API = (baseUrl.auth ?? "https://global.cilpron.com").replace(/\/+$/, "").replace(/\/api$/i, "");
const STORAGE_API_KEY = "cilpron_api_key";

const Email: React.FC = () => {
  const { user } = useAuth();
  const [apiKey, setApiKey] = useState<string | null>(() => {
    try { return localStorage.getItem(STORAGE_API_KEY); } catch { return null; }
  });

  // API: GET {GLOBAL_API}/api/AccessToken/User/{userId}  (always re-checked, so a revoked key is replaced)
  useEffect(() => {
    if (!user?.token || !user?.userId) return;
    let alive = true;
    axios.get(`${GLOBAL_API}/api/AccessToken/User/${user.userId}`, { headers: { Authorization: `Bearer ${user.token}` } })
      .then((res) => {
        const list: any[] = res.data?.["$values"] ?? (Array.isArray(res.data) ? res.data : []);
        const active = list.find((t) => !t.is_revoked && t.token_type === "ApiKey");
        if (!alive) return;
        if (active?.token) {
          setApiKey(active.token);
          try { localStorage.setItem(STORAGE_API_KEY, active.token); } catch { /* private mode */ }
        } else {
          // This user has no active key: never reuse a key left by another account.
          setApiKey(null);
          try { localStorage.removeItem(STORAGE_API_KEY); } catch { /* private mode */ }
        }
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [user?.token, user?.userId]);

  const name = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
  return <MailClient apiKey={apiKey} mailbox={user?.email ?? null} {...(name ? { displayName: name } : {})} />;
};

export default Email;